<template>
	<BaseLoading
		v-if="isLoading"
	/>
	<CForm
		v-else
		class="main-wrapper form-standard"
	>
		<CRow>
			<CCol lg="12">
				<CInput
					v-model="form.email"
					:label="`${$t('message.email_address')}*`"
					:is-valid="formState.email.valid"
					:invalid-feedback="formState.email.error"
					:disabled="formDisabled.email"
				/>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="12">
				<template>
					<CInput
						v-if="showChangePassword"
						v-model="$v.form.password.$model"
						:label="`${$t('message.password')}*`"
						:is-valid="formState.password.valid"
						:invalid-feedback="formState.password.error"
						type="password"
						autocomplete="new-password"
					/>
					<div
						v-else
						class="form-group"
					>
						<label>Password</label>
						<div class="d-block">
							<CButton
								class="btn-change-password flex transparent btn-export-csv"
								color="tertiary"
								@click="showChangePassword = true"
							>
								Change password
							</CButton>
						</div>
					</div>
				</template>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="6">
				<CInput
					v-model="$v.form.firstName.$model"
					:label="`${$t('message.first_name')}*`"
					:is-valid="formState.firstName.valid"
					:invalid-feedback="formState.firstName.error"
				/>
			</CCol>
			<CCol lg="6">
				<CInput
					v-model="form.lastName"
					:label="$t('message.last_name')"
					:description="$t('message.optional')"
				/>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="12">
				<CInput
					v-model="form.phoneNumber"
					:label="$t('message.phone_number')"
					:description="$t('message.optional')"
				/>
			</CCol>
		</CRow>
		<CRow class="container-date">
			<CCol lg="12">
				<p>
					{{ $t('message.date_registered', { date: createdAt }) }}
				</p>
				<p>
					{{ $t('message.date_modified', { date: updatedAt }) }}
				</p>
				<hr>
				<p
					:class="[{
						'text-danger': dayLeftNumber <= -1
					}]"
				>
					{{ hintMessage }}
				</p>
			</CCol>
		</CRow>
		<BaseActionSubmit
			:is-loading="isSaving"
			:submit-text="$t('button.save_change')"
			@onSubmit="handleSubmit"
		/>
	</CForm>
</template>

<script>
import dayjs from 'dayjs';
import { mapState, mapGetters, mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { inputStateValid, inputStateError, formatPassword } from '../helpers/vuelidator';

export default {
	name: 'UserCreate',

	data() {
		return {
			form: {
				email: null,
				password: null,
				firstName: null,
				lastName: null,
				phoneNumber: null,
			},
			showChangePassword: false,
		};
	},

	validations: {
		form: {
			password: {
				required(value) {
					return this.showChangePassword
						? required(value)
						: true;
				},
				formatPassword,
			},
			firstName: {
				required,
			},
		},
	},

	computed: {
		// Map State
		...mapState('me', {
			data: 'data',
			isLoading: 'isLoading',
		}),
		...mapState('meUpdate', {
			error: 'error',
			isSaving: 'isLoading',
		}),
		// Map Getter
		...mapGetters('me', {
			createdAt: 'createdAt',
			updatedAt: 'updatedAt',
		}),
		// Others Computed
		formState() {
			return {
				email: {
					valid: inputStateValid(this.$v.form.email),
					error: inputStateError(this.$v.form.email)(this),
				},
				password: {
					valid: inputStateValid(this.$v.form.password),
					error: inputStateError(this.$v.form.password)(this),
				},
				firstName: {
					valid: inputStateValid(this.$v.form.firstName),
					error: inputStateError(this.$v.form.firstName)(this),
				},
			};
		},
		formDisabled() {
			return {
				// email, role
				email: true,
			};
		},
		dayLeftNumber() {
			const diffDayNumber = dayjs().diff(this.data.password_changed_at, 'day');
			const dayLeftNumber = (90 - diffDayNumber) || 0;
			return dayLeftNumber;
		},
		hintMessage() {
			if (this.dayLeftNumber <= -1) {
				return this.$t('message.password_has_been_expired', { number: Math.abs(this.dayLeftNumber) });
			}
			return this.$t('message.days_left_change_pasword', { number: this.dayLeftNumber });
		},
	},

	async created() {
		await this.getMe();

		// set state, form
		this.setState();

		// set page title
		this.setPageTitle({
			title: this.data.name,
			isShow: true,
		});
	},

	mounted() {
		// when user passwords expired is detected then show toast.
		const detectedPasswordExpire = this.$route.query['password-expired'] !== undefined;
		if (detectedPasswordExpire) {
			this.$store.dispatch('toast/showToast', {
				id: 'alert_password_expire',
				header: 'Password Is Expired',
				content: 'You need to change your current password now.',
				type: 'danger',
				timeout: 10000000000,
			});
		}
	},

	methods: {
		// Map Actions
		...mapActions('pageTitle', {
			setPageTitle: 'setPageTitle',
		}),
		...mapActions('toast', {
			showToast: 'showToast',
			hideToast: 'hideToast',
		}),
		...mapActions('me', {
			getMe: 'getMe',
		}),
		...mapActions('meUpdate', {
			updateUserProfile: 'updateUserProfile',
		}),
		// Other Methods
		async handleSubmit() {
			this.$v.$touch();

			const isValidForm = !this.$v.$invalid;

			if (isValidForm) {
				await this.updateUserProfile({
					first_name: this.form.firstName,
					last_name: this.form.lastName,
					phone_number: this.form.phoneNumber,
					// if user change password, set password in the params
					...(
						this.showChangePassword
							? { password: this.form.password }
							: null
					),
				});

				if (this.error === null) {
					// show toast with success
					this.showToast({
						header: this.$t('toast.success'),
						content: this.$t('toast.updated_profile'),
						type: 'success',
					});
					// reset state for this component
					this.resetState();
					// refresh user info, request to api to get user again
					await this.getMe();
					// set state, form
					this.setState();
					// set page title
					this.setPageTitle({
						title: this.data.name,
						isShow: true,
					});
					// hide all toasts
					this.hideToast('warn_password_expire');
					this.hideToast('alert_password_expire');
				} else {
					// show toast with error
					this.showToast({
						header: this.$t('toast.unable_to_save'),
						content: this.error.message,
						type: 'danger',
					});
				}
			}
		},
		// side effect function, reset form
		resetState() {
			this.showChangePassword = false;
			this.form.email = null;
			this.form.password = null;
			this.form.firstName = null;
			this.form.lastName = null;
			this.form.phoneNumber = null;
		},
		// side effect function, set form
		setState() {
			this.showChangePassword = false;
			this.form.email = this.data.email;
			this.form.password = this.data.password;
			this.form.firstName = this.data.first_name;
			this.form.lastName = this.data.last_name;
			this.form.phoneNumber = this.data.phone_number;
		},
	},
};
</script>

<style lang="scss" scoped>
.container-date {
	@include typo-helper-text;

	color: $color-black-45;

	p {
		margin-bottom: rem(4);

		&:last-of-type {
			margin-bottom: 0;
		}
	}
}
</style>